import type { Condition, FieldId } from "@unbox/api";
import { XClose } from "@untitledui/icons";
import { Input } from "@/components/base/input/input";
import { Select } from "@/components/base/select/select";
import { NoCohortsEmptyState } from "@/components/creator-cohorts/no-cohorts-empty-state";
import {
  defaultConditionForField,
  fieldKind,
  NUMERIC_OPERATOR_OPTIONS,
  TAG_OPERATOR_OPTIONS,
} from "./condition-fields";

interface ConditionRowProps {
  condition: Condition;
  fieldOptions: { id: FieldId; label: string; supportingText?: string }[];
  cohortOptions: { id: string; label: string }[];
  onChange: (condition: Condition) => void;
  onRemove?: () => void;
}

export function ConditionRow({ condition, fieldOptions, cohortOptions, onChange, onRemove }: ConditionRowProps) {
  const kind = fieldKind(condition.field);
  const operatorOptions = kind === "tag" ? TAG_OPERATOR_OPTIONS : NUMERIC_OPERATOR_OPTIONS;
  const isBetween = condition.operator === "between";

  return (
    <div className="flex flex-row items-start gap-2">
      <div className="w-80">
        <Select
          size="sm"
          hideSupportingText
          items={fieldOptions}
          selectedKey={condition.field}
          onSelectionChange={(key) => {
            if (typeof key === "string") onChange(defaultConditionForField(key as FieldId));
          }}
        >
          {(item) => (
            <Select.Item id={item.id} supportingText={item.supportingText}>
              {item.label}
            </Select.Item>
          )}
        </Select>
      </div>

      <div className="w-60">
        <Select
          size="sm"
          items={operatorOptions}
          selectedKey={condition.operator}
          onSelectionChange={(key) => {
            if (typeof key !== "string") return;
            const operator = key as Condition["operator"];
            if (operator === "between" && !Array.isArray(condition.value)) {
              onChange({ ...condition, operator, value: [0, 0] } as Condition);
            } else if (operator !== "between" && Array.isArray(condition.value)) {
              onChange({ ...condition, operator, value: 0 } as Condition);
            } else {
              onChange({ ...condition, operator } as Condition);
            }
          }}
        >
          {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
        </Select>
      </div>

      <div className="flex flex-row items-center gap-2">
        {kind === "tag" ? (
          <div className="w-60">
            <Select
              size="sm"
              placeholder="Select a cohort"
              items={cohortOptions}
              selectedKey={typeof condition.value === "string" ? condition.value : null}
              onSelectionChange={(key) => {
                if (typeof key === "string") onChange({ ...condition, value: key } as Condition);
              }}
              renderEmptyState={() => <NoCohortsEmptyState />}
            >
              {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
          </div>
        ) : isBetween ? (
          <>
            <div className="w-28">
              <Input
                size="sm"
                type="number"
                value={String(Array.isArray(condition.value) ? condition.value[0] : 0)}
                onChange={(value) => {
                  const min = Number(value);
                  const max = Array.isArray(condition.value) ? condition.value[1] : 0;
                  onChange({ ...condition, value: [Number.isNaN(min) ? 0 : min, max] } as Condition);
                }}
              />
            </div>
            <span className="text-xs text-secondary">and</span>
            <div className="w-28">
              <Input
                size="sm"
                type="number"
                value={String(Array.isArray(condition.value) ? condition.value[1] : 0)}
                onChange={(value) => {
                  const max = Number(value);
                  const min = Array.isArray(condition.value) ? condition.value[0] : 0;
                  onChange({ ...condition, value: [min, Number.isNaN(max) ? 0 : max] } as Condition);
                }}
              />
            </div>
          </>
        ) : (
          <div className="w-28">
            <Input
              size="sm"
              type="number"
              value={String(typeof condition.value === "number" ? condition.value : 0)}
              onChange={(value) => {
                const num = Number(value);
                onChange({ ...condition, value: Number.isNaN(num) ? 0 : num } as Condition);
              }}
            />
          </div>
        )}
      </div>

      {onRemove && (
        <button
          type="button"
          className="p-2 text-quaternary hover:text-primary transition-colors"
          onClick={onRemove}
          aria-label="Remove condition"
        >
          <XClose className="size-4" />
        </button>
      )}
    </div>
  );
}
